<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { _battle } from "../ui/battle";
import { RoundStep } from "../core/round";
import { timingLabels } from "../core/timing";
import { missLabels } from "../core/events";

const battle = _battle;

const rounds = computed(() => battle.rounds);

const emit = defineEmits<{ close: [] }>();
const panel = ref<HTMLElement | null>(null);
let previousFocus: HTMLElement | null = null;
onMounted(() => {
    previousFocus = document.activeElement as HTMLElement | null;
    panel.value?.focus();
});
onBeforeUnmount(() => previousFocus?.focus());

/** 展开的回合 */
const openKeys = ref<string[]>([]);

// 新回合产生时自动展开
watch(
    () => battle.rounds.length,
    (n) => {
        if (n > 0) {
            const key = String(n - 1);
            if (!openKeys.value.includes(key)) {
                openKeys.value = [...openKeys.value, key];
            }
        }
    },
    { immediate: true }
);

function stepLabel(step: RoundStep): string {
    if (step.actionType === "settle") {
        if (step.operation?.kind === "switch") return `${step.side === "left" ? "挑战方" : "被挑战方"}·出手结束`;
        return step.timing ? timingLabels[step.timing] : "结算";
    }
    const sideText = step.side === "left" ? "挑战方" : "被挑战方";
    if (step.replacement)
        return `${sideText}·补位登场·${step.actorName ?? ""}`;
    if (step.actionType === "skill") return `${sideText}·${step.skillName}`;
    if (step.actionType === "switch")
        return `${sideText}·切换·${step.actorName ?? ""}`;
    if (step.actionType === "prop")
        return `${sideText}·使用·${
            step.propName ?? "道具"
        }`;
    return `${sideText}·空过`;
}

function isCurrentStep(roundIdx: number, stepIdx: number): boolean {
    return (
        battle.viewMode === "review" &&
        battle.viewRound === roundIdx &&
        battle.viewStep === stepIdx
    );
}

function isExecutingRound(roundIdx: number): boolean {
    return battle.phase === "executing" && battle.currentRoundIndex === roundIdx;
}

function reviewStep(roundIdx: number, stepIdx: number) {
    battle.review(roundIdx, stepIdx);
}

function branchAt(roundIdx: number) {
    if (!confirm(`从第 ${roundIdx + 1} 回合处重新出招？之后的记录将被丢弃。`))
        return;
    battle.branchFrom(roundIdx);
}
</script>

<template>
    <div class="battle-info-layer" @click.self="emit('close')" @keydown.esc="emit('close')">
        <section ref="panel" tabindex="-1" class="battle-info-panel" role="dialog" aria-modal="true" aria-label="对战棋谱">
            <div class="panel-header">
                <span class="panel-title">对战棋谱</span>
                <div class="header-actions">
                    <button
                        v-if="battle.viewMode === 'review'"
                        class="action-btn resume"
                        @click="battle.resumeLive()"
                    >
                        返回实时
                    </button>
                    <button class="close-btn" @click="emit('close')" title="关闭" aria-label="关闭棋谱">
                        ✕
                    </button>
                </div>
            </div>

            <div v-if="rounds.length === 0" class="empty">暂无回合记录</div>

            <div v-else class="rounds-scroll">
                <lay-collapse v-model="openKeys">
                    <lay-collapse-item
                        v-for="(round, rIdx) in rounds"
                        :key="rIdx"
                        :id="String(rIdx)"
                    >
                        <template #title>
                            <span
                                class="round-title"
                                :class="{ executing: isExecutingRound(rIdx) }"
                            >
                                第 {{ rIdx + 1 }} 回合
                            </span>
                            <span class="ops">
                                <span class="op left"
                                    >左[{{ round.describeOperation("left") }}]</span
                                >
                                <span class="op right"
                                    >右[{{ round.describeOperation("right") }}]</span
                                >
                            </span>
                        </template>

                        <div
                            class="step-row"
                            :class="{ active: isCurrentStep(rIdx, -1) }"
                            @click="reviewStep(rIdx, -1)"
                        >
                            <span class="step-no">起始</span>
                            <span class="step-label">回合开始局面</span>
                        </div>

                        <div
                            v-for="(step, sIdx) in round.steps"
                            :key="sIdx"
                            class="step-row"
                            :class="{ active: isCurrentStep(rIdx, sIdx) }"
                            @click="reviewStep(rIdx, sIdx)"
                        >
                            <span class="step-no">{{ sIdx + 1 }}</span>
                            <span class="step-label">{{ stepLabel(step) }}</span>
                            <span
                                v-for="name in step.defeatedNames ?? []"
                                :key="name"
                                class="tag defeat"
                                >{{ name }} 阵亡</span
                            >
                            <span v-if="step.result?.missReason" class="tag miss">{{ missLabels[step.result.missReason] }}</span>
                            <span v-if="step.result?.hit && !step.result.hitEffectsApplied" class="tag miss">命中附效失效</span>
                            <span v-if="step.notExecuted" class="tag miss"
                                >{{
                                    step.blockReason
                                        ? `因【${step.blockReason}】无法行动`
                                        : "未能出招"
                                }}</span
                            >
                            <span v-if="step.actionType === 'settle'" class="tag settle"
                                >结算</span
                            >
                            <span v-else-if="step.replacement" class="tag switch">登场</span>
                            <span
                                v-else-if="step.actionType === 'skill'"
                                class="tag skill"
                                >技能</span
                            >
                            <span
                                v-else-if="step.actionType === 'switch'"
                                class="tag switch"
                                >切换</span
                            >
                            <span
                                v-else-if="step.actionType === 'prop'"
                                class="tag prop"
                                >道具</span
                            >
                            <span v-else class="tag skip">空过</span>
                        </div>

                        <button class="branch-btn" @click.stop="branchAt(rIdx)">
                            从此处重新出招
                        </button>
                    </lay-collapse-item>
                </lay-collapse>
            </div>
        </section>
    </div>
</template>

<style scoped>
.battle-info-layer {
    position: absolute;
    inset: 0;
    z-index: 15;
    display: grid;
    place-items: center;
    pointer-events: auto;
    background: rgba(0, 0, 0, 0.55);
}

.battle-info-panel {
    box-sizing: border-box;
    width: calc(var(--ui-unit) * 640);
    max-width: 94%;
    height: calc(var(--ui-unit) * 460);
    max-height: 94%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #0c0c18;
    border: var(--ui-unit) solid rgba(255, 215, 0, 0.25);
    border-radius: calc(var(--ui-unit) * 12);
    box-shadow: 0 calc(var(--ui-unit) * 12) calc(var(--ui-unit) * 40) rgba(0, 0, 0, 0.5);
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(var(--ui-unit) * 12) calc(var(--ui-unit) * 14);
    border-bottom: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.06);
}

.panel-title {
    color: #ffd700;
    font-weight: 700;
    font-size: calc(var(--ui-unit) * 15);
    text-shadow: 0 0 calc(var(--ui-unit) * 10) rgba(255, 215, 0, 0.25);
}

.header-actions {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 8);
}

.action-btn {
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 10);
    background: rgba(45, 45, 45, 0.9);
    color: #ffd700;
    border: calc(var(--ui-unit) * 1) solid #ffd700;
    border-radius: calc(var(--ui-unit) * 4);
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 11);
    transition: all 0.2s;
}

.action-btn:hover {
    background: #ffd700;
    color: #1a1a1a;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(var(--ui-unit) * 22);
    height: calc(var(--ui-unit) * 22);
    padding: 0;
    background: rgba(255, 255, 255, 0.05);
    color: #888;
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-radius: calc(var(--ui-unit) * 4);
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 11);
    transition: all 0.2s;
}

.close-btn:hover {
    color: #e0e0e0;
    background: rgba(255, 255, 255, 0.1);
}

.empty {
    color: #666;
    text-align: center;
    padding: calc(var(--ui-unit) * 24) 0;
    font-size: calc(var(--ui-unit) * 13);
}

.rounds-scroll {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    padding: calc(var(--ui-unit) * 8) calc(var(--ui-unit) * 10);
}

.round-title {
    font-weight: 600;
    color: #c0c0d0;
    margin-right: calc(var(--ui-unit) * 8);
    font-size: calc(var(--ui-unit) * 13);
}

.round-title.executing {
    color: #ffd700;
    text-shadow: 0 0 calc(var(--ui-unit) * 8) rgba(255, 215, 0, 0.3);
}

.ops {
    display: inline-flex;
    gap: calc(var(--ui-unit) * 8);
    flex-wrap: wrap;
}

.op {
    font-size: calc(var(--ui-unit) * 11);
}

.op.left {
    color: #3b82f6;
}

.op.right {
    color: #ec4899;
}

.step-row {
    display: flex;
    align-items: center;
    gap: calc(var(--ui-unit) * 8);
    padding: calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 6);
    margin: calc(var(--ui-unit) * 1) 0;
    border-radius: calc(var(--ui-unit) * 4);
    cursor: pointer;
    border: calc(var(--ui-unit) * 1) solid transparent;
    transition: background 0.12s, border-color 0.12s;
}

.step-row:hover {
    background: rgba(255, 255, 255, 0.04);
}

.step-row.active {
    background: rgba(255, 215, 0, 0.08);
    border-color: rgba(255, 215, 0, 0.3);
}

.step-no {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: calc(var(--ui-unit) * 24);
    height: calc(var(--ui-unit) * 18);
    background: rgba(255, 255, 255, 0.06);
    color: #a0a0b0;
    border-radius: calc(var(--ui-unit) * 3);
    font-size: calc(var(--ui-unit) * 10);
    font-weight: 700;
    flex-shrink: 0;
}

.step-label {
    color: #c0c0d0;
    font-size: calc(var(--ui-unit) * 12);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tag {
    margin-left: auto;
    padding: calc(var(--ui-unit) * 1) calc(var(--ui-unit) * 6);
    border-radius: calc(var(--ui-unit) * 8);
    font-size: calc(var(--ui-unit) * 10);
    font-weight: 600;
    flex-shrink: 0;
}

.tag.skill {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
}

.tag.switch {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
}

.tag.prop {
    background: rgba(34, 197, 94, 0.15);
    color: #22c55e;
}

.tag.skip {
    background: rgba(107, 114, 128, 0.12);
    color: #888;
}

.tag.defeat {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
}

.tag.miss {
    background: rgba(107, 114, 128, 0.12);
    color: #90a4ae;
}

.tag.settle {
    background: rgba(168, 85, 247, 0.15);
    color: #a855f7;
}

.branch-btn {
    display: block;
    margin: calc(var(--ui-unit) * 6) 0 calc(var(--ui-unit) * 2) auto;
    padding: calc(var(--ui-unit) * 2) calc(var(--ui-unit) * 10);
    background: transparent;
    color: #f59e0b;
    border: calc(var(--ui-unit) * 1) solid rgba(245, 158, 11, 0.3);
    border-radius: calc(var(--ui-unit) * 4);
    cursor: pointer;
    font-size: calc(var(--ui-unit) * 11);
    transition: all 0.2s;
}

.branch-btn:hover {
    background: rgba(245, 158, 11, 0.12);
    border-color: #f59e0b;
}


</style>
